/**
 * Permission checks against the stored account role (`user.role`).
 * Current reader/writer mode is resolved separately in `src/auth/roleUtils.js`.
 * Both modules stay separate so mode switching does not grant or remove edit rights.
 */

/**
 * Normalize role string (handles case variations)
 */
export const normalizeRole = (role) => {
  if (!role) return "";
  return role.toLowerCase().trim();
};

/**
 * Check if user has a specific role
 */
export const hasRole = (user, role) => {
  if (!user || !role) return false;
  return normalizeRole(user.role) === normalizeRole(role);
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (user, roles) => {
  if (!user || !roles || !Array.isArray(roles)) return false;
  const userRole = normalizeRole(user.role);
  return roles.some(role => normalizeRole(role) === userRole);
};

/**
 * Check if user is SuperAdmin
 */
export const isSuperAdmin = (user) => {
  if (!user?.role) return false;
  const r = normalizeRole(user.role);
  if (r === "superadmin" || r === "admin") return true;
  return hasAnyRole(user, ["superadmin", "superAdmin", "SuperAdmin", "Admin"]);
};

/**
 * Check if user is Writer
 */
export const isWriter = (user) => {
  return hasRole(user, "writer");
};

/**
 * Check if user is Reader
 */
export const isReader = (user) => {
  return hasRole(user, "reader");
};

/**
 * Check if user can engage with content (like, comment, bookmark).
 * Both readers and writers can engage; writers use the same account to read and interact.
 */
export const canEngageWithContent = (user) => {
  return hasAnyRole(user, ["reader", "writer"]);
};

/**
 * Get dashboard route based on user role
 */
const DASHBOARD_ROUTES = {
  superadmin: "/admin/dashboard",
  admin: "/admin/dashboard",
  writer: "/app/dashboard",
  reader: "/app/dashboard",
};

export const getDashboardRoute = (user) => {
  if (!user || !user.role) return "/app/dashboard";
  const role = normalizeRole(user.role);
  return DASHBOARD_ROUTES[role] ?? "/app/dashboard";
};

/**
 * Check if user can edit content
 * Writers edit their own work; SuperAdmin can moderate any item.
 */
export const canEditContent = (user, contentItem) => {
  if (!user || !contentItem) return false;

  if (isSuperAdmin(user)) return true;

  if (isWriter(user)) {
    // Writers can only edit their own content
    // Check if content was created by this writer
    const userName = user.displayName || user.email || user.uid;
    const createdBy = contentItem.createdBy || contentItem.createdByEmail || contentItem.createdByName || contentItem.authorName;
    return createdBy === userName || createdBy === user.email || contentItem.createdBy === user.uid;
  }
  
  return false;
};

/**
 * Check if user can delete content
 * Same rules as edit (writers: own items; SuperAdmin: any).
 */
export const canDeleteContent = (user, contentItem) => {
  return canEditContent(user, contentItem);
};

/**
 * Check if user can create content
 * Writers create as authors; SuperAdmin can create for moderation / seeding.
 */
export const canCreateContent = (user) => {
  if (!user) return false;
  return isWriter(user) || isSuperAdmin(user);
};

